
import { TeamScheduleAdminScreenProps } from "./TeamScheduleAdminStack";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
  usePaginationFragment,
} from "react-relay";
import { TeamScheduleAdminArchivedScreenList_archiveRoutines$key } from "./__generated__/TeamScheduleAdminArchivedScreenList_archiveRoutines.graphql";
import { FlashList } from "@shopify/flash-list";
import React, { useCallback } from "react";
import { TeamScheduleRoutineListItemContent } from "../Team/TeamHomeNavigator/TeamScheduleListItem/TeamScheduleRoutineListItemContent";
import { TeamScheduleAdminArchivedScreenQuery } from "./__generated__/TeamScheduleAdminArchivedScreenQuery.graphql";
import { AHSeparator, Box, ListItem } from "arkh";
import { OrgButton } from "../OrgHome/OrgButton";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { TeamScheduleAdminArchivedScreenListItem_routine$key } from "./__generated__/TeamScheduleAdminArchivedScreenListItem_routine.graphql";
import { useAlert } from "@/src/common/Alert";
import { useIntl } from "react-intl";

export function TeamScheduleAdminArchivedScreen(
  props: TeamScheduleAdminScreenProps<"TeamScheduleAdminArchived">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <TeamScheduleAdminArchived {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function TeamScheduleAdminArchived(
  props: TeamScheduleAdminScreenProps<"TeamScheduleAdminArchived"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<TeamScheduleAdminArchivedScreenQuery>(
    graphql`
      query TeamScheduleAdminArchivedScreenQuery(
        $teamId: ID!
        $after: String
        $first: Int
        $query: String
      ) {
        store {
          team(id: $teamId) {
            id
            name
            ...TeamScheduleAdminArchivedScreenList_archiveRoutines
          }
        }
      }
    `,
    {
      teamId: props.route.params.teamId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const onUnarchived = useCallback(
    (routineId: string) => {
      props.navigation.goBack();
      props.navigation.navigate("RoutineEdit", {
        id: routineId,
      });
    },
    [props.navigation]
  );
  if (!data.store.team) {
    return null;
  }
  return (
    <TeamScheduleAdminArchivedList
      team={data.store.team}
      onUnarchived={onUnarchived}
    />
  );
}

function TeamScheduleAdminArchivedList(props: {
  team: TeamScheduleAdminArchivedScreenList_archiveRoutines$key;
  onUnarchived: (routineId: string) => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment TeamScheduleAdminArchivedScreenList_archiveRoutines on Team
      @refetchable(
        queryName: "TeamScheduleAdminArchivedList_TeamRefetchQuery"
      ) {
        id
        archivedRoutines(first: $first, after: $after, query: $query)
          @connection(
            key: "TeamScheduleAdminArchivedList_routines_archivedRoutines"
          ) {
          edges {
            cursor
            node {
              id
              ...TeamScheduleAdminArchivedScreenListItem_routine
            }
          }
        }
      }
    `,
    props.team
  );

  const loadMore = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(30);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  type Item = (typeof data.archivedRoutines.edges)[number];
  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      return (
        <TeamScheduleAdminArchivedListItem
          routine={item.node}
          onUnarchived={props.onUnarchived}
        />
      );
    },
    [props.onUnarchived]
  );
  return (
    <FlashList
      data={data.archivedRoutines.edges}
      ItemSeparatorComponent={AHSeparator}
      onEndReached={loadMore}
      onEndReachedThreshold={0.5}
      renderItem={renderItem}
    />
  );
}

function TeamScheduleAdminArchivedListItem(props: {
  routine: TeamScheduleAdminArchivedScreenListItem_routine$key;
  onUnarchived: (routineId: string) => void;
}) {
  const intl_tokens = useIntlTokens();

  const routine = useFragment(
    graphql`
      fragment TeamScheduleAdminArchivedScreenListItem_routine on Routine {
        id
        title
        ...TeamScheduleRoutineListItemContent_Routine
      }
    `,
    props.routine
  );

  const [unarchiveMutation, isUnarchiving] = useMutation(graphql`
    mutation TeamScheduleAdminArchivedScreenListItem_unarchiveMutation(
      $id: ID!
    ) {
      routine(id: $id) {
        unarchiveRoutine {
          id
          isArchived
        }
      }
    }
  `);
  const { onUnarchived } = props;
  const Alert = useAlert();

  const intl = useIntl();
  const onPressUnarchive = useCallback(() => {
    Alert.confirm(
      intl.formatMessage(
        {
          defaultMessage: `Unarchive "{routineTitle}"`,
        },
        {
          routineTitle: routine.title,
        }
      ),
      intl.formatMessage({
        defaultMessage: "Are you sure you want to unarchive this routine?",
      })
    ).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      unarchiveMutation({
        variables: {
          id: routine.id,
        },
        onCompleted: () => {
          onUnarchived(routine.id);
        },
      });
    });
  }, [Alert, intl, routine.title, routine.id, unarchiveMutation, onUnarchived]);

  return (
    <ListItem
      right={
        <OrgButton
          title={intl_tokens.Unarchive}
          backgroundColor="surface-background-light"
          tintColor="text-primary"
          onPress={onPressUnarchive}
          loading={isUnarchiving}
        />
      }
    >
      <Box style={{ flex: 1 }} spaceVertical="small">
        <TeamScheduleRoutineListItemContent routine={routine} occasion={null} />
      </Box>
    </ListItem>
  );
}
